import React, { useRef, useLayoutEffect, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PageWrapper from './PageWrapper';
import EditableText from './EditableText';
import { selectPageSlice } from '../Selector';
import { reportPageCount } from '../../../redux/actions';

const PAGE_HEIGHT = 1123;
const PADDING_TOP = 64;
const PADDING_BOTTOM = 60;
const FOOTER_HEIGHT = 40;
const CONTENT_HEIGHT = PAGE_HEIGHT - PADDING_TOP - PADDING_BOTTOM - FOOTER_HEIGHT; // 959px
const CONTENT_WIDTH = 794 - 60 - 60; // 674px

// `.priority-table-wrapper` has margin-top: 16px which getBoundingClientRect()
// doesn't include in row/header measurements. Reserve it plus a small buffer so
// the last row's bottom border doesn't get clipped by the page's overflow:hidden.
const TABLE_WRAPPER_OFFSET = 20;

// The footer's legend (`.rater-legend`) has a top margin (14px) that the footer
// element's getBoundingClientRect() height does NOT include, so the measured
// footer height is short by that much. Without accounting for it, a footer that
// *barely* fits passes the fit check and then overflows into the page footer
// band, clipping the bottom legend circles. Reserve the legend's top margin plus
// a small safety buffer so a borderline footer is pushed to its own page instead.
const FOOTER_FIT_BUFFER = 24;

// Colors fixed to the standard rater groups. These letters always keep their
// color regardless of what `legendKeys` contains.
const FIXED_ICON_COLORS = {
  S: '#0171cf',
  M: '#797979',
  O: '#cbcbcb',
};

// The full pool of six colors used across the application. Any legend letter
// without a fixed color is assigned an unused color from this pool. Since
// `legendKeys` holds at most 5 items, a free color is always available.
const ICON_COLOR_POOL = ['#0171cf', '#797979', '#83bcf0', '#444444', '#1c51a6', '#cbcbcb'];

const ICON_TEXT_STYLE = { color: '#fff', border: 'none' };

// Build a letter -> color map for a single report render. Fixed letters keep
// their color; any other letter takes the next color from the pool that isn't
// already claimed by a fixed color or a previously assigned letter. Assignment
// follows the order letters appear in `legendKeys` so colors stay consistent.
function buildIconColorMap(legendKeys) {
  const colorByLetter = { ...FIXED_ICON_COLORS };
  const used = new Set(Object.values(FIXED_ICON_COLORS));
  const available = ICON_COLOR_POOL.filter(color => !used.has(color));

  (legendKeys || []).forEach(({ letter }) => {
    if (!letter || colorByLetter[letter]) return;
    const next = available.shift();
    if (next) colorByLetter[letter] = next;
  });

  return colorByLetter;
}

const DEFAULT_SCALE_HEADERS = ['Never', 'Rarely', 'Occasionally', 'Sometimes', 'Often', 'Always'];

function normalizeHeader(value, fallback) {
  if (value == null) return fallback;
  return String(value)
    .trim()
    // Rejoin words split with a hard hyphen + line break in saved config data
    // (e.g. "Occasion-\nally" → "Occasionally") so labels print as one word and
    // wrap naturally, without a stray mid-word hyphen.
    .replace(/-\s*\n\s*/g, '')
    .replace(/\s+/g, ' ');
}

function getHeaderConfig(tableContents) {
  const h = tableContents.headers && tableContents.headers[0];
  if (!h) {
    return {
      categoryTop: 'PILLAR - COMPETENCY',
      categoryBottom: 'BEHAVIOR',
      scaleHeaders: DEFAULT_SCALE_HEADERS,
      priorityVotes: 'PRIORITY VOTES',
    };
  }
  return {
    categoryTop:  'PILLAR - COMPETENCY',
    categoryBottom: "BEHAVIOR",
    scaleHeaders: DEFAULT_SCALE_HEADERS,
    priorityVotes: normalizeHeader(h.priorityVotes, 'PRIORITY VOTES').toUpperCase(),
  };
}

function parseRow(row) {
  const combined = String(row[0] || '');
  const [pillarRaw, behaviorRaw = ''] = combined.split('$$**');
  return {
    pillar: (pillarRaw || '').trim(),
    behavior: behaviorRaw.trim(),
    scalePositions: [row[1], row[2], row[3], row[4], row[5], row[6]].map(c => (Array.isArray(c) ? c : [])),
    votes: row[7],
  };
}

function RaterIcon({ label, size = 'md', colorMap }) {
  const background = (colorMap && colorMap[label]) || FIXED_ICON_COLORS.O;
  return (
    <span
      className={`rater-icon rater-icon--${size}`}
      style={{
        backgroundColor: background,
        color: ICON_TEXT_STYLE.color,
        border: ICON_TEXT_STYLE.border,
      }}
    >
      {label}
    </span>
  );
}

function PriorityTableHead({ headerConfig }) {
  return (
    <thead>
      <tr>
        <th className="col-category">
          <div className="col-category__top">{headerConfig.categoryTop}</div>
          <div className="col-category__bottom">{headerConfig.categoryBottom}</div>
        </th>
        {headerConfig.scaleHeaders.map((label, idx) => (
          <th key={idx} className="col-scale">{label}</th>
        ))}
        <th className="col-votes">{headerConfig.priorityVotes}</th>
      </tr>
    </thead>
  );
}

function PriorityRowCells({ row, colorMap }) {
  const { pillar, behavior, scalePositions, votes } = parseRow(row);
  return (
    <>
      <td className="col-category">
        <div className="col-category__pillar">{pillar}</div>
        {behavior && <div className="col-category__behavior">{behavior}</div>}
      </td>
      {scalePositions.map((icons, colIdx) => (
        <td key={colIdx} className="col-scale">
          <div className="icon-stack">
            {icons.map((icon, iconIdx) => (
              <RaterIcon key={iconIdx} label={icon} colorMap={colorMap} />
            ))}
          </div>
        </td>
      ))}
      <td className="col-votes">{votes}</td>
    </>
  );
}

function TableFooter({ summaryContentPath, legendKeys, colorMap }) {
  return (
    <>
      {summaryContentPath && (
        <EditableText
          as="div"
          className="pdf-table-footer-text"
          path={summaryContentPath}
          variant="block"
        />
      )}
      <div className="rater-legend">
        {(legendKeys || []).map(({ letter, description }) => (
          <div key={letter} className="rater-legend__item">
            <RaterIcon label={letter} size="md" colorMap={colorMap} />
            <span>{description}</span>
          </div>
        ))}
      </div>
    </>
  );
}

function PaginatedPrioritySection({
  header,
  tableContents,
  legendKeys,
  summaryContentPath,
  slicePageContent,
  startPageNumber,
  personName,
  slice,
  sectionKey,
  reportPageCount,
}) {
  const rows = (tableContents && tableContents.contents) || [];
  const headerConfig = getHeaderConfig(tableContents || {});
  const colorMap = buildIconColorMap(legendKeys);

  const measureRef = useRef(null);
  const [pageRowGroups, setPageRowGroups] = useState(null);
  const [footerOnNewPage, setFooterOnNewPage] = useState(false);

  const isFirstSliceRef = useRef(true);
  useEffect(() => {
    if (isFirstSliceRef.current) {
      isFirstSliceRef.current = false;
      return;
    }
    setPageRowGroups(null);
  }, [slice]);

  useLayoutEffect(() => {
    if (pageRowGroups) return;
    if (!measureRef.current) return;

    const container = measureRef.current;
    const headerEl = container.querySelector('[data-pp="header"]');
    const theadEl = container.querySelector('[data-pp="thead"]');
    const footerEl = container.querySelector('[data-pp="footer"]');
    const trEls = Array.from(container.querySelectorAll('tr[data-pp-row]'));

    const tableEl = container.querySelector('[data-pp="table"]');

    const headerH = headerEl ? headerEl.getBoundingClientRect().height : 0;
    const theadH = theadEl ? theadEl.getBoundingClientRect().height : 0;
    const footerH = footerEl ? footerEl.getBoundingClientRect().height : 0;
    const actualTableH = tableEl ? tableEl.getBoundingClientRect().height : 0;
    const rowHeights = trEls.map(tr => tr.getBoundingClientRect().height);

    const sumOfRowH = rowHeights.reduce((s, h) => s + h, 0);
    const tableOverhead = Math.max(0, actualTableH - theadH - sumOfRowH);

    const page1Available = CONTENT_HEIGHT - headerH - theadH - tableOverhead - TABLE_WRAPPER_OFFSET;
    const restAvailable = CONTENT_HEIGHT - theadH - tableOverhead - TABLE_WRAPPER_OFFSET;

    const allPages = [];
    let pageRows = [];
    let usedH = 0;
    let isFirstPage = true;

    rows.forEach((row, idx) => {
      const h = rowHeights[idx] || 0;
      const avail = isFirstPage ? page1Available : restAvailable;

      if (pageRows.length > 0 && usedH + h > avail) {
        allPages.push(pageRows);
        pageRows = [row];
        usedH = h;
        isFirstPage = false;
      } else {
        pageRows.push(row);
        usedH += h;
      }
    });

    if (pageRows.length > 0) {
      allPages.push(pageRows);
    }
    const lastPage = allPages[allPages.length - 1] || [];
    const lastPageAvail = allPages.length <= 1 ? page1Available : restAvailable;
    const lastPageUsed = lastPage.reduce((sum, _, i) => {
      const globalIdx = rows.length - lastPage.length + i;
      return sum + (rowHeights[globalIdx] || 0);
    }, 0);

    const needsFooterPage = lastPageUsed + footerH + FOOTER_FIT_BUFFER > lastPageAvail;

    setFooterOnNewPage(needsFooterPage);
    const groups = allPages.length > 0 ? allPages : [rows];
    setPageRowGroups(groups);

    if (sectionKey && reportPageCount) {
      reportPageCount(sectionKey, groups.length + (needsFooterPage ? 1 : 0));
    }
  }, [pageRowGroups, rows, sectionKey, reportPageCount]);

  // ── Phase 1: measurement ────────────────────────────────────────────────────
  if (!pageRowGroups) {
    return (
      <div style={{ position: 'relative', height: 0, overflow: 'hidden' }}>
        <div
          ref={measureRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: CONTENT_WIDTH,
            visibility: 'hidden',
            pointerEvents: 'none',
          }}
        >
          <div data-pp="header">{header}</div>
          <div className="priority-table-wrapper">
            <table data-pp="table" className="pdf-table pdf-table--priority">
              <PriorityTableHead headerConfig={headerConfig} />
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={idx} data-pp-row={idx}>
                    <PriorityRowCells row={row} colorMap={colorMap} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div data-pp="footer">
            <TableFooter
              summaryContentPath={summaryContentPath}
              legendKeys={legendKeys}
              colorMap={colorMap}
            />
          </div>
        </div>
      </div>
    );
  }

  // ── Phase 2: render pages ───────────────────────────────────────────────────
  return (
    <>
      {pageRowGroups.map((pageRows, idx) => {
        const isFirst = idx === 0;
        const isLast = idx === pageRowGroups.length - 1;
        const showFooter = isLast && !footerOnNewPage;

        return (
          <PageWrapper
            key={idx}
            pageNumber={startPageNumber + idx}
            personName={personName}
          >
            {isFirst && header}
            <div className="priority-table-wrapper">
              <table className="pdf-table pdf-table--priority">
                <PriorityTableHead headerConfig={headerConfig} />
                <tbody>
                  {pageRows.map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      <PriorityRowCells row={row} colorMap={colorMap} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {showFooter && (
              <TableFooter
                summaryContentPath={summaryContentPath}
                legendKeys={legendKeys}
                colorMap={colorMap}
              />
            )}
          </PageWrapper>
        );
      })}
      {footerOnNewPage && (
        <PageWrapper
          pageNumber={startPageNumber + pageRowGroups.length}
          personName={personName}
        >
          <TableFooter
            summaryContentPath={summaryContentPath}
            legendKeys={legendKeys}
            colorMap={colorMap}
          />
        </PageWrapper>
      )}
    </>
  );
}

const mapStateToProps = (state, ownProps) => ({
  slice: selectPageSlice(state, ownProps.slicePageContent),
});

const mapDispatchToProps = (dispatch) => ({
  reportPageCount: (sectionKey, count) => dispatch(reportPageCount(sectionKey, count)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PaginatedPrioritySection);
